import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Check, Loader2, Lock, Pin, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Author, Box, ForumComment, KindBadge, VoteArrows } from "@/components/ForumComment";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

/** The nested tree the API sends, laid out flat with its depth kept — nothing recurses in JSX. */
const flatten = (list, out = []) => {
  (list || []).forEach((post) => {
    out.push(post);
    flatten(post.children, out);
  });
  return out;
};

const Chip = ({ icon: Icon, label, tone, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tone}`}
  >
    <Icon className="h-3 w-3" /> {label}
  </span>
);

export const ForumThread = ({ threadId, onBack, onChanged, canReply, canModerate }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/forum/${threadId}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(apiError(e));
        onBack();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const after = () => {
    load();
    onChanged?.();
  };

  const failed = (e) => toast.error(apiError(e));

  const voteComment = (post, value) =>
    api.post(`/forum/comments/${post.id}/vote`, { value }).then(after).catch(failed);

  const voteThread = (value) =>
    api.post(`/forum/${threadId}/vote`, { value }).then(after).catch(failed);

  const post = (body, parentId) => {
    if (!body.trim()) {
      toast.error("Write something before you post it.");
      return Promise.resolve(false);
    }
    setPosting(true);
    return api
      .post(`/forum/${threadId}/comments`, { body, parent_id: parentId || null })
      .then(() => {
        toast.success("Your post is on the thread.");
        after();
        return true;
      })
      .catch((e) => {
        failed(e);
        return false;
      })
      .finally(() => setPosting(false));
  };

  const accept = (comment) =>
    api
      .post(`/forum/${threadId}/accept`, { comment_id: comment.accepted ? "" : comment.id })
      .then(after)
      .catch(failed);

  const editComment = (comment, body) =>
    api
      .patch(`/forum/comments/${comment.id}`, { body })
      .then(() => {
        toast.success("Your post is corrected.");
        load();
        return true;
      })
      .catch((e) => {
        failed(e);
        return false;
      });

  const removeComment = (comment) =>
    api
      .delete(`/forum/comments/${comment.id}`)
      .then(() => {
        toast.success("The post is off the thread.");
        after();
      })
      .catch(failed);

  const moderate = (patch, said) =>
    api
      .patch(`/forum/${threadId}`, patch)
      .then(() => {
        toast.success(said);
        after();
      })
      .catch(failed);

  const removeThread = () =>
    api
      .delete(`/forum/${threadId}`)
      .then(() => {
        toast.success("The thread is off the forum.");
        onChanged?.();
        onBack();
      })
      .catch(failed);

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center" data-testid="forum-thread-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const t = data.thread;
  const posts = flatten(data.comments);
  const canAccept = (t.mine || data.can_moderate) && t.kind === "question";
  const replyWord = t.kind === "question" ? "answer" : "reply";

  return (
    <div data-testid="forum-thread">
      <button
        type="button"
        data-testid="forum-back"
        onClick={onBack}
        className="label-caps inline-flex items-center gap-1 text-muted-foreground transition-colors duration-200 hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All threads
      </button>

      <div className="mt-4 border border-border bg-card p-5">
        <div className="flex items-start gap-4">
          <VoteArrows votes={t.votes} my={t.my_vote} testId="vote-thread" onVote={voteThread} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <KindBadge kind={t.kind} />
              {t.pinned ? (
                <Chip
                  icon={Pin}
                  label="Pinned by SDTD"
                  tone="border-primary text-primary"
                  testId="thread-pinned"
                />
              ) : null}
              {t.locked ? (
                <Chip
                  icon={Lock}
                  label="Locked"
                  tone="border-[hsl(var(--watch))] text-[hsl(var(--watch))]"
                  testId="thread-locked"
                />
              ) : null}
              {t.answered ? (
                <Chip
                  icon={Check}
                  label="Answered"
                  tone="border-[hsl(var(--healthy))] text-[hsl(var(--healthy))]"
                  testId="thread-answered"
                />
              ) : null}
            </div>
            <h1
              className="mt-3 font-display text-2xl font-black tracking-tight sm:text-3xl"
              data-testid="thread-title"
            >
              {t.title}
            </h1>
            <div className="mt-2">
              <Author post={t} />
            </div>
            {t.body ? (
              <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed">{t.body}</p>
            ) : null}
            {t.tags?.length ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {t.tags.map((tag) => (
                  <span
                    key={tag}
                    className="border border-border px-2 py-0.5 text-[11px] text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}

            {canModerate ? (
              <div
                className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4"
                data-testid="thread-moderation"
              >
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none"
                  data-testid="thread-pin"
                  onClick={() =>
                    moderate(
                      { pinned: !t.pinned },
                      t.pinned ? "The thread is unpinned." : "The thread is pinned to the top.",
                    )
                  }
                >
                  <Pin className="mr-1.5 h-3.5 w-3.5" /> {t.pinned ? "Unpin" : "Pin"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none"
                  data-testid="thread-lock"
                  onClick={() =>
                    moderate(
                      { locked: !t.locked },
                      t.locked ? "The thread is open again." : "The thread is locked.",
                    )
                  }
                >
                  <Lock className="mr-1.5 h-3.5 w-3.5" /> {t.locked ? "Unlock" : "Lock"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none text-[hsl(var(--at-risk))]"
                  data-testid="thread-remove"
                  onClick={removeThread}
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remove the thread
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <h2 className="label-caps mt-6" data-testid="forum-reply-count">
        {posts.length} {replyWord}(s)
      </h2>

      {canReply && !t.locked ? (
        <div className="mt-3 border border-border bg-card p-4" data-testid="forum-answer-box">
          <Box
            value={draft}
            onChange={setDraft}
            rows={4}
            testId="forum-answer-input"
            placeholder={
              t.kind === "question"
                ? "Answer from what your unit has actually done — the officer asking will see it."
                : "Add to the discussion…"
            }
          />
          <Button
            className="mt-3 rounded-none"
            disabled={posting}
            data-testid="forum-answer-submit"
            onClick={() => post(draft).then((ok) => ok && setDraft(""))}
          >
            {posting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Post the {replyWord}
          </Button>
        </div>
      ) : null}

      {t.locked ? (
        <p
          className="mt-3 border border-border bg-muted p-4 text-sm text-muted-foreground"
          data-testid="forum-locked-note"
        >
          SDTD has locked this thread. It stays on record, but nothing further can be added.
        </p>
      ) : null}

      <div className="mt-4 space-y-2">
        {posts.map((c) => (
          <ForumComment
            key={c.id}
            post={c}
            canAccept={canAccept}
            canReply={canReply && !t.locked}
            onVote={voteComment}
            onPost={post}
            onAccept={accept}
            onEdit={editComment}
            onRemove={removeComment}
          />
        ))}
        {!posts.length ? (
          <p className="border border-border bg-card p-6 text-center text-sm text-muted-foreground">
            Nobody has replied yet. If you know the answer, you are the one this thread is waiting
            for.
          </p>
        ) : null}
      </div>
    </div>
  );
};
